import * as RadixDialog from "@radix-ui/react-dialog";
import * as React from "react";
import { cn } from "./cn";

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Content>
>(({ className, children, ...props }, ref) => (
  <RadixDialog.Portal>
    {/* Overlay: Full-screen backdrop with fade in animation */}
    <RadixDialog.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-black/0",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "duration-500",
      )}
    />
    {/* Wrapper: Centers the dialog using flexbox instead of translate */}
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Content: The actual dialog box - animations apply cleanly without positioning conflicts */}
      <RadixDialog.Content
        ref={ref}
        className={cn(
          "grid w-full max-w-md gap-4 rounded-lg border border-neutral-500 border-offset-8 bg-neutral-900/50 p-6 text-white/80 shadow-2xl",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:slide-out-to-bottom-8 data-[state=open]:slide-in-from-bottom-8",
          "duration-500",
          className,
        )}
        {...props}
      >
        {children}
      </RadixDialog.Content>
    </div>
  </RadixDialog.Portal>
));
DialogContent.displayName = "DialogContent";

export const DialogHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn("flex flex-col items-center justify-center text-center mb-4", className)} {...props} />
);

export const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof RadixDialog.Title>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(({ className, ...props }, ref) => (
  <RadixDialog.Title ref={ref} className={cn("text-lg font-semibold text-white", className)} {...props} />
));
DialogTitle.displayName = RadixDialog.Title.displayName;

export const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof RadixDialog.Description>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(({ className, ...props }, ref) => (
  <RadixDialog.Description ref={ref} className={cn("text-sm text-neutral-400", className)} {...props} />
));
DialogDescription.displayName = RadixDialog.Description.displayName;

export const DialogFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
